/* eslint-disable react-hooks/refs */
import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import './ReviewCarousel.css';

const InflationInvaders = lazy(() => import('./InflationInvaders'));

const reviews = [
  { name: "Student 1", text: "He is simply the best—incredibly passionate, incredibly detailed and knowledgeable. His effort in planning events, and organizing things for us, went above and beyond. Dedicated individual." },
  { name: "Student 2", text: "Elijah was one of the best TAs I have had at Cornell--it was clear that he was genuinely invested in his student's success, and he consistently worked to engage students in both his section and lecture." },
  { name: "Student 3", text: "Elijah did a wonderful job organizing the discussion sections - I was never pressured to speak but also felt motivated to do so as he created a very relaxed yet stimulating environment." },
  { name: "Student 4", text: "He was the most passionate TA I've ever had. He cares so much about all of his students and ensuring we learned a lot while incorporating fun." },
  { name: "Student 5", text: "Elijah was very creative and came up with fun, engaging activities for discussion. I felt like I got a lot out of these activities. He was also very approachable for general questions." },
  { name: "Student 6", text: "Discussions were very engaging. He was very good at playing to his students strengths and interests to get the entire class engaged." },
  { name: "Student 7", text: "A very kind and down to earth person. One of my favorite TAs at Cornell." },
  { name: "Student 8", text: "Incredibly helpful TA. He spent (what looks like) hours working through the datasets that I sent him and he provided extremely thorough feedback." },
  { name: "Student 9", text: "Elijah was always willing to offer time to support us, whether it be through reviewing papers or explaining difficult concepts." },
  { name: "Student 10", text: "Elijah is great at coming up with innovative activities and games that help students learn the material. He is also very approachable and kind." },
  { name: "Student 11", text: "Elijah is passionate about the subject matter and very engaging in his explanation of the content. He is personable and welcoming, and encourages us to engage with the course material WITH him as opposed to speaking at us." },
  { name: "Student 12", text: "Always replied to my emails with great detail and appreciated the time and willingness to meet for any help!" },
  { name: "Student 13", text: "The review sessions that were in class were really engaging and very fun. great way to have students involved and review topics. one of my favorite activities in the class by far." },
  { name: "Student 14", text: "I love how Elijah challenges me to think outside my current perspective by asking me hypothetical questions. I think his feedback on my essays was very helpful too." },
  { name: "Student 15", text: "I think that this student gave lots of needed critiques on my work, and also taught everything in a very clear and concise way." },
  { name: "Student 16", text: "He was very knowledgeable of the course material and focuses on Indigenous people which was cool to hear his lecture where we learned more about what he is studying. Discussion was also interesting and engaging." },
  { name: "Student 17", text: "Elijah was very knowledgable and supportive throughout the course, and good at explaining material during the discussion." },
];

function shuffleArray(arr) {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export default function ReviewCarousel() {
  const [queue] = useState(() => shuffleArray(reviews));
  // messages can be student reviews or CTA bubbles: { type: 'review', ...review } or { type: 'cta' }
  const [messages, setMessages] = useState([]);
  const [showTyping, setShowTyping] = useState(true);
  const [hearted, setHearted] = useState({});
  const [inputText, setInputText] = useState('');
  const [showGame, setShowGame] = useState(false);
  const nextIndex = useRef(0);
  const timerRef = useRef(null);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  const addMessageRef = useRef(null);

  const addMessage = useCallback(() => {
    if (nextIndex.current >= queue.length) return;
    const idx = nextIndex.current;
    nextIndex.current += 1;
    setShowTyping(false);
    setMessages(prev => [...prev, { type: 'review', ...queue[idx] }]);

    if (nextIndex.current < queue.length) {
      timerRef.current = setTimeout(() => {
        setShowTyping(true);
        timerRef.current = setTimeout(() => addMessageRef.current?.(), 1500);
      }, 2000);
    }
  }, [queue]);

  useEffect(() => {
    addMessageRef.current = addMessage;
  }, [addMessage]);

  useEffect(() => {
    timerRef.current = setTimeout(addMessage, 1500);
    return () => clearTimeout(timerRef.current);
  }, [addMessage]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, showTyping]);

  const toggleHeart = (index) => {
    setHearted(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleSend = () => {
    const text = inputText.trim() || "Hey, let's connect!";
    setMessages(prev => [...prev, { type: 'sent', text }]);
    setInputText('');
    // Open mailto with their typed message as the body
    const subject = encodeURIComponent("Let's connect!");
    const body = encodeURIComponent(text);
    setTimeout(() => {
      window.location.href = `mailto:esm247@cornell.edu?subject=${subject}&body=${body}`;
    }, 400);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="imsg">
      <div className="imsg__window">
        {/* macOS title bar */}
        <div className="imsg__titlebar">
          <div className="imsg__traffic-lights">
            <span className="imsg__tl imsg__tl--close" />
            <span className="imsg__tl imsg__tl--minimize" />
            <span className="imsg__tl imsg__tl--maximize" />
          </div>
          <span className="imsg__titlebar-text">Messages</span>
        </div>

        <div className="imsg__content">
          {/* Sidebar */}
          <div className="imsg__sidebar">
            <div className="imsg__search">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <circle cx="5" cy="5" r="4" stroke="#8e8e93" strokeWidth="1.2"/>
                <line x1="8" y1="8" x2="11" y2="11" stroke="#8e8e93" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              <span>Search</span>
            </div>
            <div className="imsg__conv imsg__conv--active">
              <div className="imsg__conv-avatar">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <circle cx="16" cy="16" r="15" fill="#e5e5ea"/>
                  <circle cx="11" cy="12" r="3" fill="#b0b0b5"/>
                  <circle cx="21" cy="12" r="3" fill="#b0b0b5"/>
                  <circle cx="16" cy="22" r="3" fill="#b0b0b5"/>
                  <circle cx="11" cy="12" r="1.8" fill="#d1d1d6"/>
                  <circle cx="21" cy="12" r="1.8" fill="#d1d1d6"/>
                  <circle cx="16" cy="22" r="1.8" fill="#d1d1d6"/>
                </svg>
              </div>
              <div className="imsg__conv-info">
                <span className="imsg__conv-name">Former Cornell Students</span>
                <span className="imsg__conv-preview">Tap to see reviews...</span>
              </div>
            </div>
          </div>

          {/* Main chat area */}
          <div className="imsg__chat">
            {/* Chat header */}
            <div className="imsg__chat-header">
              <div className="imsg__chat-header-avatar">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <circle cx="18" cy="18" r="17" fill="#e5e5ea"/>
                  <circle cx="12" cy="13" r="3.5" fill="#b0b0b5"/>
                  <circle cx="24" cy="13" r="3.5" fill="#b0b0b5"/>
                  <circle cx="18" cy="24" r="3.5" fill="#b0b0b5"/>
                  <circle cx="12" cy="13" r="2" fill="#d1d1d6"/>
                  <circle cx="24" cy="13" r="2" fill="#d1d1d6"/>
                  <circle cx="18" cy="24" r="2" fill="#d1d1d6"/>
                </svg>
              </div>
              <div className="imsg__chat-header-info">
                <span className="imsg__chat-header-name">Former Cornell Students</span>
                <span className="imsg__chat-header-count">{reviews.length} members</span>
              </div>
            </div>

            {/* Messages body */}
            <div className="imsg__body" ref={bodyRef}>
              {messages.map((msg, i) =>
                msg.type === 'sent' ? (
                  <div key={i} className="imsg__message-group imsg__message-group--sent">
                    <div className="imsg__bubble imsg__bubble--blue">
                      <p className="imsg__text imsg__text--white">{msg.text}</p>
                    </div>
                  </div>
                ) : (
                  <div key={i} className="imsg__message-group">
                    <span className="imsg__sender">{msg.name}</span>
                    <div
                      className={`imsg__bubble${hearted[i] ? ' imsg__bubble--hearted' : ''}`}
                      onClick={() => toggleHeart(i)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleHeart(i); }}
                    >
                      <p className="imsg__text">{msg.text}</p>
                      {hearted[i] && (
                        <span className="imsg__heart" aria-label="Loved">❤️</span>
                      )}
                    </div>
                  </div>
                )
              )}
              {showTyping && nextIndex.current < queue.length && (
                <div className="imsg__message-group">
                  <span className="imsg__sender">{queue[nextIndex.current].name}</span>
                  <div className="imsg__typing">
                    <span className="imsg__dot" />
                    <span className="imsg__dot" />
                    <span className="imsg__dot" />
                  </div>
                </div>
              )}
            </div>

            {/* Input bar */}
            <div className="imsg__input-bar">
              <button className="imsg__plus-btn" aria-label="Play a game" type="button" onClick={() => setShowGame(true)}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9.5" stroke="#8e8e93" strokeWidth="1"/>
                  <line x1="10" y1="5.5" x2="10" y2="14.5" stroke="#8e8e93" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="5.5" y1="10" x2="14.5" y2="10" stroke="#8e8e93" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
              <input
                ref={inputRef}
                className="imsg__input-real"
                type="text"
                placeholder="iMessage"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button
                className={`imsg__send-btn${inputText.trim() ? ' imsg__send-btn--active' : ''}`}
                onClick={handleSend}
                aria-label="Send"
                type="button"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" fill="currentColor"/>
                  <path d="M12 7l0 10M12 7l-3.5 3.5M12 7l3.5 3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Game modal */}
      {showGame && (
        <div className="imsg__game-modal" onClick={() => setShowGame(false)}>
          <div className="imsg__game-modal-inner" onClick={(e) => e.stopPropagation()}>
            <Suspense fallback={<div style={{ color: '#4fc3f7', textAlign: 'center', padding: 40, fontFamily: 'monospace' }}>LOADING...</div>}>
              <InflationInvaders onClose={() => setShowGame(false)} />
            </Suspense>
          </div>
        </div>
      )}
    </div>
  );
}
